// src/api/csrf.ts

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://api.wbl-dartliga.de/api"
    : import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

/**
 * Utility to get a cookie value by name.
 */
export function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift();
  return undefined;
}

function absoluteApiUrl(endpoint: string) {
  let url = API_BASE_URL.replace(/\/$/, "");
  let ep = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  // Remove double /api if present
  if (url.endsWith("/api") && ep.startsWith("/api/")) {
    ep = ep.replace(/^\/api/, "");
  }
  return url + ep;
}

/**
 * Fetches the CSRF cookie from the backend if not present.
 */
export async function ensureCsrfCookie(endpoint: string = "/csrf") {
  let csrftoken = getCookie("csrftoken") || getCookie("XSRF-TOKEN");
  if (!csrftoken) {
    await fetch(absoluteApiUrl(endpoint), { credentials: "include" });
  }
}

/**
 * Returns headers with CSRF token if available.
 */
export function withCsrfHeaders(
  headers: Record<string, string> = {}
): Record<string, string> {
  const csrftoken = getCookie("csrftoken") || getCookie("XSRF-TOKEN");
  if (csrftoken) {
    return { ...headers, "X-CSRFToken": csrftoken };
  }
  return headers;
}

/**
 * A fetch wrapper that always includes credentials and CSRF token if available.
 * Ensures CSRF cookie is set before making unsafe requests (POST/PUT/PATCH/DELETE).
 */
export async function csrfFetch(
  input: RequestInfo,
  init: RequestInit = {},
  csrfEndpoint: string = "/users/csrf/"
): Promise<Response> {
  const method = (init.method || "GET").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    await ensureCsrfCookie(csrfEndpoint);
    init.headers = withCsrfHeaders(init.headers as Record<string, string>);
  }
  // If input is a string and looks like a relative API path, always use API_BASE_URL in prod
  let url = input;
  if (
    typeof input === "string" &&
    input.startsWith("/api/") &&
    process.env.NODE_ENV === "production"
  ) {
    url = absoluteApiUrl(input.replace(/^\/api/, ""));
  }
  return fetch(url, { ...init, credentials: "include" });
}
